import { Link } from "react-router-dom";
import { useActivityByOwner } from "@/pages/activity/hooks/useActivityByOwner";
import { Truncate } from "@/design-system/components/ui";
import { relativeTime } from "@/components/ux/relativeTime";

/**
 * Layout Standard §4.1 — right-rail "Recent activity" preview card on
 * an entity's Overview tab. Read-only, last 5 cross-module events, with
 * a "See all activity →" link that deep-links to the Activity tab on
 * the same entity (per Layout Standard §6.2 URL persistence).
 *
 * Sources the same `/api/activity/by-owner/{ownerType}/{ownerId}`
 * endpoint as <c>ActivityTabPanel</c> so a freshly logged call or
 * completed task on the Communications / Tasks tab shows up here
 * without a separate query path.
 *
 * Generalized in Layout Compliance v1.0 PR 6 from the lead-only
 * <c>LeadOverviewActivityPreview</c> introduced in PR 5. Each entity
 * detail page passes its own owner slug + base path so the link target
 * stays correct.
 */
interface Props {
  /** Lower-snake-case owner slug, e.g. "lead", "broker". */
  ownerType: string;
  /** Owner row id (string-formatted Guid). */
  ownerId: string;
  /**
   * Detail-page base path for the "See all activity →" link, e.g.
   * "/leads/abc". The component appends `?tab=activity`.
   */
  detailPath: string;
  /** Optional override for the data-testid. Defaults to the owner slug. */
  testId?: string;
}

const PREVIEW_COUNT = 5;

export function OverviewActivityPreview({
  ownerType,
  ownerId,
  detailPath,
  testId,
}: Props) {
  const list = useActivityByOwner(ownerType, ownerId, { take: PREVIEW_COUNT });
  const events = list.data?.data ?? [];

  return (
    <section
      aria-label="Recent activity"
      className="border border-border-light rounded-md bg-white-pure p-space-3 min-w-0"
      data-testid={testId ?? `${ownerType}-overview-activity-preview`}
    >
      <div className="flex items-center justify-between mb-space-2">
        <h3 className="font-display font-bold text-h4 text-onyx m-0">
          Recent activity
        </h3>
        <Link
          to={`${detailPath}?tab=activity`}
          className="text-caption text-gold-shadow hover:underline"
        >
          See all activity →
        </Link>
      </div>

      {list.isLoading && (
        <p className="text-small text-gray-mid m-0">Loading…</p>
      )}

      {!list.isLoading && events.length === 0 && (
        <p className="text-small text-gray-mid m-0">No activity yet.</p>
      )}

      {events.length > 0 && (
        <ul className="m-0 p-0 list-none divide-y divide-border-light">
          {events.map((event) => (
            <li key={event.id} className="py-space-2 first:pt-0 last:pb-0">
              <div className="flex items-baseline justify-between gap-space-2 min-w-0">
                {/* §11.4 — revealed on hover AND on keyboard focus, through
                    the portaled Tooltip. The native `title` this replaces never
                    opens on focus, so it satisfied the hover half of the
                    contract and silently failed the other. */}
                <Truncate lines={1} revealOnlyWhenClamped className="text-small text-onyx min-w-0">
                  {event.headline}
                </Truncate>
                <span className="text-label text-gray-mid shrink-0">
                  {relativeTime(event.occurredAt)}
                </span>
              </div>
              <div className="text-label text-gray-mid mt-space-1 break-words">
                {event.actorDisplayName}
                {event.subline ? ` · ${event.subline}` : ""}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
